import React from "react";
import {
    Alert,
    Button,
    Descriptions,
    Divider,
    Drawer,
    Empty,
    Space,
    Table,
    Tag,
    Typography,
    Upload,
    Tooltip,
} from "antd";
import {
    EditOutlined,
    EyeOutlined,
    DownloadOutlined,
    UploadOutlined,
} from "@ant-design/icons";
import {
    formatAmount,
    formatDateTime,
    getActionColor,
    getActionLabel,
    getChildFillTypeColor,
    getFillTypeLabel,
    getParentStatusColor,
    getParentStatusLabel,
} from "./transactionScheduleUi";
import { isEmptySchedule } from "./transactionScheduleRules";
import TransactionScheduleAttachments from "./TransactionScheduleAttachments";
import dayjs from "dayjs";

const { Text } = Typography;

function TransactionScheduleDetailDrawer({
    open,
    onClose,
    selectedRecord,
    detailData,
    statusLogs,
    loading,
    onManage,
    attachments = [],
    onViewGeneratedSlipPdf,
    onDownloadGeneratedSlipPdf,
    onViewGeneratedSampleFilledPdf,
    onDownloadGeneratedSampleFilledPdf,
    onUploadScannedLatestSlipPdf,
    onUploadScannedLatestSampleFilledPdf,
    onViewScannedLatestSlipPdf,
    onDownloadScannedLatestSlipPdf,
    onViewScannedLatestSampleFilledPdf,
    onDownloadScannedLatestSampleFilledPdf,

}) {
    const isEmpty = isEmptySchedule(selectedRecord);

    const allocationColumns = [
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 90,
        },
        {
            title: "Дүн",
            dataIndex: "allocatedAmount",
            key: "allocatedAmount",
            width: 140,
            render: (value) => formatAmount(value),
        },
        {
            title: "Төлөв",
            dataIndex: "fillType",
            key: "fillType",
            width: 130,
            render: (value) => (
                <Tag color={getChildFillTypeColor(value)}>
                    {getFillTypeLabel(value)}
                </Tag>
            ),
        },
        {
            title: "PDF",
            key: "pdfs",
            width: 420,
            render: (_, row) => {
                const agreed = row.agreed === true;

                return (
                    <Space direction="vertical" size={10}>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>Slip PDF</div>
                            <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>
                                {row.generatedSlipPdfFileName || "-"}
                            </div>

                            <Space size={6} wrap>
                                <Tooltip title="Үүсгэсэн PDF харах">
                                    <Button
                                        size="small"
                                        icon={<EyeOutlined />}
                                        disabled={!row.generatedSlipPdfFileName}
                                        onClick={() => onViewGeneratedSlipPdf?.(row)}
                                    />
                                </Tooltip>

                                <Tooltip title="Үүсгэсэн PDF татах">
                                    <Button
                                        size="small"
                                        icon={<DownloadOutlined />}
                                        disabled={!row.generatedSlipPdfFileName}
                                        onClick={() => onDownloadGeneratedSlipPdf?.(row)}
                                    />
                                </Tooltip>

                                <Upload
                                    accept="application/pdf"
                                    showUploadList={false}
                                    beforeUpload={(file) => {
                                        onUploadScannedLatestSlipPdf?.(row, file);
                                        return false;
                                    }}
                                    disabled={!agreed}
                                >
                                    <Tooltip title={agreed ? "Scanned slip PDF оруулах" : "Эхлээд зөвшөөрөх шаардлагатай"}>
                                        <Button size="small" icon={<UploadOutlined />} disabled={!agreed} />
                                    </Tooltip>
                                </Upload>

                                <Tooltip title="Scanned slip PDF харах">
                                    <Button
                                        size="small"
                                        icon={<EyeOutlined />}
                                        disabled={!row.scannedLatestSlipPdfFileName}
                                        onClick={() => onViewScannedLatestSlipPdf?.(row)}
                                    />
                                </Tooltip>

                                <Tooltip title="Scanned slip PDF татах">
                                    <Button
                                        size="small"
                                        icon={<DownloadOutlined />}
                                        disabled={!row.scannedLatestSlipPdfFileName}
                                        onClick={() => onDownloadScannedLatestSlipPdf?.(row)}
                                    />
                                </Tooltip>
                            </Space>

                            {row.scannedLatestSlipPdfFileName ? (
                                <div style={{ fontSize: 11, color: "#52c41a", marginTop: 4 }}>
                                    Scanned: {row.scannedLatestSlipPdfFileName}
                                </div>
                            ) : null}
                        </div>

                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>Sample Filled PDF</div>
                            <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>
                                {row.generatedSampleFilledPdfFileName || "-"}
                            </div>

                            <Space size={6} wrap>
                                <Tooltip title="Үүсгэсэн PDF харах">
                                    <Button
                                        size="small"
                                        icon={<EyeOutlined />}
                                        disabled={!row.generatedSampleFilledPdfFileName}
                                        onClick={() => onViewGeneratedSampleFilledPdf?.(row)}
                                    />
                                </Tooltip>

                                <Tooltip title="Үүсгэсэн PDF татах">
                                    <Button
                                        size="small"
                                        icon={<DownloadOutlined />}
                                        disabled={!row.generatedSampleFilledPdfFileName}
                                        onClick={() => onDownloadGeneratedSampleFilledPdf?.(row)}
                                    />
                                </Tooltip>

                                <Upload
                                    accept="application/pdf"
                                    showUploadList={false}
                                    beforeUpload={(file) => {
                                        onUploadScannedLatestSampleFilledPdf?.(row, file);
                                        return false;
                                    }}
                                    disabled={!agreed}
                                >
                                    <Tooltip title={agreed ? "Scanned sample filled PDF оруулах" : "Эхлээд зөвшөөрөх шаардлагатай"}>
                                        <Button size="small" icon={<UploadOutlined />} disabled={!agreed} />
                                    </Tooltip>
                                </Upload>

                                <Tooltip title="Scanned sample filled PDF харах">
                                    <Button
                                        size="small"
                                        icon={<EyeOutlined />}
                                        disabled={!row.scannedLatestSampleFilledPdfFileName}
                                        onClick={() => onViewScannedLatestSampleFilledPdf?.(row)}
                                    />
                                </Tooltip>

                                <Tooltip title="Scanned sample filled PDF татах">
                                    <Button
                                        size="small"
                                        icon={<DownloadOutlined />}
                                        disabled={!row.scannedLatestSampleFilledPdfFileName}
                                        onClick={() => onDownloadScannedLatestSampleFilledPdf?.(row)}
                                    />
                                </Tooltip>
                            </Space>

                            {row.scannedLatestSampleFilledPdfFileName ? (
                                <div style={{ fontSize: 11, color: "#52c41a", marginTop: 4 }}>
                                    Scanned: {row.scannedLatestSampleFilledPdfFileName}
                                </div>
                            ) : null}
                        </div>
                    </Space>
                );
            },
        },
        {
            title: "Approved At",
            dataIndex: "approvedAt",
            key: "approvedAt",
            width: 170,
            render: (value) =>
                value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "-",
        }
        // {
        //     title: "Үүсгэсэн огноо",
        //     dataIndex: "createdAt",
        //     key: "createdAt",
        //     width: 180,
        //     render: (value) => formatDateTime(value),
        // },
    ];

    const logColumns = [
        {
            title: "Огноо",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 180,
            render: (value) => formatDateTime(value),
        },
        {
            title: "Үйлдэл",
            dataIndex: "actionType",
            key: "actionType",
            width: 140,
            render: (value) => (
                <Tag color={getActionColor(value)}>
                    {getActionLabel(value)}
                </Tag>
            ),
        },
        {
            title: "Хуваарилалтын ID",
            dataIndex: "transactionScheduleAllocationId",
            key: "transactionScheduleAllocationId",
            width: 150,
        },
    ];

    return (
        <Drawer
            title="Гүйлгээний хуваарийн дэлгэрэнгүй"
            placement="right"
            width={960}
            open={open}
            onClose={onClose}
            destroyOnClose={false}
        >
            {!selectedRecord ? (
                <Empty description="Мэдээлэл сонгогдоогүй байна" />
            ) : (
                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                    <Descriptions
                        bordered
                        size="small"
                        column={{ xs: 1, sm: 2, md: 2 }}
                    >
                        <Descriptions.Item label="ID">
                            {selectedRecord.id}
                        </Descriptions.Item>
                        <Descriptions.Item label="Хүсэлтийн дугаар">
                            {selectedRecord.requestNumber || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Төлөгч">
                            {selectedRecord.payer || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Төлбөрийн дүн">
                            {formatAmount(selectedRecord.paymentAmount)} {selectedRecord.currency || ""}
                        </Descriptions.Item>
                        <Descriptions.Item label="Төлөв">
                            <Tag color={getParentStatusColor(selectedRecord.parentDisplayStatus)}>
                                {getParentStatusLabel(selectedRecord.parentDisplayStatus)}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Үлдэгдэл дүн">
                            {formatAmount(selectedRecord.remainingAmount)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Хүлээн авагч байгууллага">
                            {selectedRecord.beneficiaryCompany || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Банк">
                            {selectedRecord.beneficiaryBank || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Данс">
                            {selectedRecord.beneficiaryAccount || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Огноо">
                            {formatDateTime(selectedRecord.createdAt)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Гүйлгээний утга" span={2}>
                            {selectedRecord.paymentPurpose || "-"}
                        </Descriptions.Item>
                    </Descriptions>

                    <TransactionScheduleAttachments attachments={attachments} />

                    {isEmpty ? (
                        <>
                            <Alert
                                type="info"
                                showIcon
                                message="Энэ хүсэлт дээр хуваарилалт үүсээгүй байна"
                                description="Үндсэн мэдээлэл болон хавсралтуудыг харах боломжтой. Хуваарилалт хийх бол доорх товчийг ашиглана."
                            />

                            <Space>
                                <Button
                                    type="primary"
                                    icon={<EditOutlined />}
                                    onClick={() => onManage?.(selectedRecord)}
                                >
                                    Хуваарь удирдах
                                </Button>
                            </Space>
                        </>
                    ) : (
                        <>
                            <Space
                                align="center"
                                style={{
                                    justifyContent: "space-between",
                                    width: "100%",
                                }}
                            >
                                <Text strong>Хуваарилалтын мэдээлэл</Text>
                                <Button
                                    icon={<EditOutlined />}
                                    onClick={() => onManage?.(selectedRecord)}
                                >
                                    Хуваарь удирдах
                                </Button>
                            </Space>

                            <Table
                                rowKey={(row) => row.id}
                                loading={loading}
                                columns={allocationColumns}
                                dataSource={detailData?.allocations || []}
                                pagination={false}
                                scroll={{ x: 700 }}
                                size="small"
                            />
                        </>
                    )}

                    <Divider style={{ margin: "8px 0" }} />

                    <Text strong>Төлөв өөрчлөлтийн түүх</Text>
                    <Table
                        rowKey={(row, index) => row.id || index}
                        loading={loading}
                        columns={logColumns}
                        dataSource={statusLogs || []}
                        pagination={{ pageSize: 5 }}
                        scroll={{ x: 650 }}
                        size="small"
                    />
                </Space>
            )}
        </Drawer>
    );
}

export default TransactionScheduleDetailDrawer;